import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Text,
  XAxis,
  YAxis,
} from "recharts";

import { type BarChartMetric } from "@vega-api/generated/types/barChartMetric";
import { type BarChartMetricAllOfBarChartData } from "@vega-api/generated/types/barChartMetricAllOfBarChartData";
import { BarChartMetricAllOfBarChartDataOrientation } from "@vega-api/generated/types/barChartMetricAllOfBarChartDataOrientation";
import { BarChartMetricAllOfBarChartDataValueFormat } from "@vega-api/generated/types/barChartMetricAllOfBarChartDataValueFormat";
import { AnalyticCard } from "@vega-core/components/MetricCard/AnalyticCard";
import {
  ChartContainer,
  ChartTooltip,
} from "@vega-core/components/shadcn/chart";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { getChartColors } from "@vega-core/helpers/getChartColors";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatMoneyShort } from "@vega-core/utils/formatMoney";
import { formatNumber } from "@vega-core/utils/formatNumber";
import { createUrlSlug } from "@vega-core/utils/url.helper";

import { ChartNoData } from "../ChartNoData/ChartNoData";
import { ChartValueTooltip } from "../ChartValueTooltip/ChartValueTooltip";

const LOADING_PLACEHOLDERS = Array.from({ length: 3 }, (_, index) => index);

const CHART_COLOR = getChartColors(0);

export const CHART_HEIGHT_CLASS_NAME = "h-72 w-full";

const CHART_CONFIG = {
  value: {
    color: CHART_COLOR,
  },
};

type BarPoint = {
  label: string;
  value: number;
};

function isBarPoint(point: unknown): point is BarPoint {
  // recharts types payload as broad unknown — narrow it explicitly
  if (typeof point !== "object" || point === null) return false;

  return (
    "label" in point &&
    "value" in point &&
    typeof point.label === "string" &&
    typeof point.value === "number"
  );
}

const TICK_LABEL_MAX_LENGTH = 24;
const ANGLED_TICK_LABEL_MAX_LENGTH = 18;
const ANGLED_TICK_THRESHOLD = 10;
const ANGLED_TICK_AXIS_HEIGHT = 88;
const DEFAULT_TICK_AXIS_HEIGHT = 30;
const MAX_BAR_SIZE = 64;
const VERTICAL_VALUE_LABEL_MAX_BARS = 6;

const truncateTo = (maxLength: number) => (label: string) =>
  label.length > maxLength ? `${label.slice(0, maxLength)}…` : label;

type CategoryTickConfig = {
  angle: number;
  maxLength: number;
  textAnchor: "end" | "middle" | "start";
  verticalAnchor: "end" | "middle" | "start";
  width?: number;
};

const renderCategoryTick =
  ({
    angle,
    maxLength,
    textAnchor,
    verticalAnchor,
    width,
  }: CategoryTickConfig) =>
  ({
    x,
    y,
    payload,
  }: {
    x?: number;
    y?: number;
    payload?: { value?: unknown };
  }) => {
    const fullLabel = String(payload?.value ?? "");
    const displayLabel = truncateTo(maxLength)(fullLabel);
    const showTitle = angle !== 0 || displayLabel !== fullLabel;

    return (
      <g>
        {showTitle ? <title>{fullLabel}</title> : null}
        <Text
          angle={angle}
          textAnchor={textAnchor}
          verticalAnchor={verticalAnchor}
          width={width}
          x={x}
          y={y}
        >
          {displayLabel}
        </Text>
      </g>
    );
  };

function formatBarValue({
  barChartData,
  value,
}: {
  barChartData: BarChartMetricAllOfBarChartData;
  value: number;
}): string {
  if (
    barChartData.valueFormat ===
      BarChartMetricAllOfBarChartDataValueFormat.CURRENCY &&
    barChartData.currency
  ) {
    return formatMoneyShort({ amount: value, currency: barChartData.currency });
  }

  return formatNumber(value);
}

function BarTooltip({
  active,
  payload,
  barChartData,
}: {
  active?: boolean;
  barChartData: BarChartMetricAllOfBarChartData;
  payload?: { payload?: unknown }[];
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;

  if (!isBarPoint(point)) return null;

  return (
    <ChartValueTooltip
      label={point.label}
      value={formatBarValue({ barChartData, value: point.value })}
    />
  );
}

function BarChartBody({
  barChartData,
}: {
  barChartData: BarChartMetricAllOfBarChartData;
}) {
  if (barChartData.bars.length === 0) {
    return (
      <ChartNoData
        className={CHART_HEIGHT_CLASS_NAME}
        dataTestId="bar-chart-no-data"
        message={m.coInvestments_insights_chart_noData()}
      />
    );
  }

  const isHorizontal =
    barChartData.orientation ===
    BarChartMetricAllOfBarChartDataOrientation.HORIZONTAL;
  const hasAngledTicks =
    !isHorizontal &&
    barChartData.bars.some((bar) => bar.label.length > ANGLED_TICK_THRESHOLD);
  const formatValue = (value: number) =>
    formatBarValue({ barChartData, value });
  const showValueLabels =
    isHorizontal || barChartData.bars.length <= VERTICAL_VALUE_LABEL_MAX_BARS;

  return (
    <ChartContainer className={CHART_HEIGHT_CLASS_NAME} config={CHART_CONFIG}>
      <BarChart
        data={barChartData.bars}
        layout={isHorizontal ? "vertical" : "horizontal"}
        margin={{ top: 24, right: isHorizontal ? 48 : 8, bottom: 0, left: 8 }}
      >
        <CartesianGrid
          horizontal={!isHorizontal}
          strokeDasharray="3 3"
          strokeOpacity={0.5}
          vertical={isHorizontal}
        />
        {/* recharts drops axes wrapped in fragments — keep each axis a direct child */}
        {isHorizontal ? (
          <XAxis
            axisLine={false}
            tickFormatter={formatValue}
            tickLine={false}
            tickMargin={8}
            type="number"
          />
        ) : (
          <XAxis
            axisLine={false}
            dataKey="label"
            height={
              hasAngledTicks
                ? ANGLED_TICK_AXIS_HEIGHT
                : DEFAULT_TICK_AXIS_HEIGHT
            }
            interval={0}
            tick={renderCategoryTick({
              angle: hasAngledTicks ? -45 : 0,
              maxLength: hasAngledTicks
                ? ANGLED_TICK_LABEL_MAX_LENGTH
                : TICK_LABEL_MAX_LENGTH,
              textAnchor: hasAngledTicks ? "end" : "middle",
              verticalAnchor: "start",
            })}
            tickLine={false}
            tickMargin={8}
            type="category"
          />
        )}
        {isHorizontal ? (
          <YAxis
            axisLine={false}
            dataKey="label"
            tick={renderCategoryTick({
              angle: 0,
              maxLength: TICK_LABEL_MAX_LENGTH,
              textAnchor: "end",
              verticalAnchor: "middle",
              width: 132,
            })}
            tickLine={false}
            tickMargin={8}
            type="category"
            width={140}
          />
        ) : (
          <YAxis
            axisLine={false}
            tickFormatter={formatValue}
            tickLine={false}
            tickMargin={8}
            type="number"
            width={64}
          />
        )}
        <ChartTooltip
          content={(tooltipProps) => (
            <BarTooltip
              active={tooltipProps.active}
              barChartData={barChartData}
              payload={tooltipProps.payload}
            />
          )}
          cursor={false}
        />
        <Bar
          dataKey="value"
          fill={CHART_COLOR}
          maxBarSize={MAX_BAR_SIZE}
          radius={4}
        >
          {showValueLabels ? (
            <LabelList
              className="body-small-medium"
              dataKey="value"
              formatter={formatValue}
              position={isHorizontal ? "right" : "top"}
            />
          ) : null}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

export const BarChartMetricCard = ({ metric }: { metric: BarChartMetric }) => (
  <AnalyticCard
    canScreenShot
    dataTestId={`bar-chart-${createUrlSlug(metric.label)}`}
    title={metric.label}
  >
    <BarChartBody barChartData={metric.barChartData} />
  </AnalyticCard>
);

type BarChartMetricsProps = {
  barChartMetrics: BarChartMetric[];
  isLoading?: boolean;
};

export const BarChartMetrics = ({
  barChartMetrics,
  isLoading,
}: BarChartMetricsProps) => {
  if (!isLoading && barChartMetrics.length === 0) {
    return null;
  }

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
      data-testid="insights-bar-charts"
    >
      {isLoading
        ? LOADING_PLACEHOLDERS.map((id) => (
            <AnalyticCard
              dataTestId={`bar-chart-loading-${id}`}
              isLoading
              key={id}
              title=""
            >
              <Skeleton
                className={cnTwMerge("mt-4", CHART_HEIGHT_CLASS_NAME)}
              />
            </AnalyticCard>
          ))
        : barChartMetrics.map((metric) => (
            <BarChartMetricCard key={metric.label} metric={metric} />
          ))}
    </div>
  );
};
